import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtErr, isAuPhone } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { document.title = "Log In or Sign Up | Zaekids Laundromat"; }, []);
  useEffect(() => {
    if (user) navigate(location.state?.from || (user.role === "admin" ? "/admin" : "/account"));
  }, [user, navigate, location.state]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && form.phone && !isAuPhone(form.phone)) {
      toast.error("Please enter a valid Australian phone number");
      return;
    }
    setBusy(true);
    try {
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(`/auth/${mode}`, payload);
      if (data.token) localStorage.setItem("zk_token", data.token);
      setUser(data);
      toast.success(mode === "login" ? `Welcome back, ${data.name}!` : "Account created — welcome to Zaekids!");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-3 text-sm font-semibold outline-none transition-colors duration-200 focus:border-brand-green focus:ring-4 focus:ring-brand-green/10";

  return (
    <div data-testid="auth-page" className="mx-auto max-w-md px-4 py-16 sm:px-6">
      <div className="text-center">
        <span className="font-script text-3xl font-bold text-brand-yellow-dark">Welcome!</span>
        <h1 className="mt-2 text-3xl font-black tracking-tight text-brand-green sm:text-4xl">
          {mode === "login" ? "Log in to your account" : "Create your account"}
        </h1>
        <p className="mt-3 text-sm text-slate-500">Save addresses, see booking history and re-book in one tap. Guests can still book without an account.</p>
      </div>
      <div className="mt-8 flex rounded-full bg-soft-green p-1">
        {["login", "register"].map((m) => (
          <button key={m} data-testid={`auth-tab-${m}`} onClick={() => setMode(m)}
            className={`flex-1 rounded-full py-2.5 text-sm font-extrabold transition-colors duration-200 ${mode === m ? "bg-brand-green text-white" : "text-brand-green"}`}>
            {m === "login" ? "Log In" : "Sign Up"}
          </button>
        ))}
      </div>
      <form onSubmit={submit} className="mt-6 rounded-3xl bg-white p-8 shadow-[0_8px_30px_rgb(27,94,32,0.08)]">
        {mode === "register" && (
          <>
            <label className="mb-1 block text-sm font-extrabold text-brand-green">Full name *</label>
            <input data-testid="auth-name-input" className={input} required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Smith" />
            <label className="mb-1 mt-4 block text-sm font-extrabold text-brand-green">Phone (optional)</label>
            <input data-testid="auth-phone-input" className={input} value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="04XX XXX XXX" />
          </>
        )}
        <label className="mb-1 mt-4 block text-sm font-extrabold text-brand-green">Email *</label>
        <input data-testid="auth-email-input" type="email" className={input} required value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@email.com" />
        <label className="mb-1 mt-4 block text-sm font-extrabold text-brand-green">Password *</label>
        <input data-testid="auth-password-input" type="password" className={input} required minLength={6} value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" />
        <button type="submit" disabled={busy} data-testid="auth-submit-btn"
          className="btn-bounce mt-6 w-full rounded-full bg-brand-green px-8 py-4 font-extrabold text-white disabled:opacity-60">
          {busy ? "One moment…" : mode === "login" ? "Log In" : "Create Account"}
        </button>
      </form>
    </div>
  );
}
